import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ClipboardList, RefreshCw, Shield, ShieldAlert, ShieldCheck, ShieldX, Lock, KeyRound, AlertTriangle, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, Button, Spinner } from '@/components/ui';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { useToast } from '@/components/ui/Toast';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';

interface LoginAttempt {
  id: string;
  email: string;
  ipAddress: string;
  eventType: 'password_verified' | 'password_failed' | 'rate_limited' | 'mfa_success' | 'mfa_failed' | 'mfa_skipped';
  success: boolean;
  userId?: string;
  details?: string;
  timestamp: string | null;
}

interface IpSummary {
  ip: string;
  last24h: number;
  last6h: number;
  last1h: number;
  suspicious: boolean;
}

const EVENT_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  password_verified: { label: 'Password OK', icon: ShieldCheck, color: 'text-green-600' },
  password_failed: { label: 'Wrong Password', icon: ShieldX, color: 'text-red-600' },
  rate_limited: { label: 'Rate Limited', icon: Lock, color: 'text-orange-600' },
  mfa_success: { label: 'MFA Verified', icon: Shield, color: 'text-green-600' },
  mfa_failed: { label: 'MFA Failed', icon: ShieldAlert, color: 'text-red-600' },
  mfa_skipped: { label: 'MFA Skipped', icon: KeyRound, color: 'text-blue-600' },
};

function formatTimestamp(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function AuditLogSection() {
  const toast = useToast();
  const { confirmWarning } = useConfirm();
  const [attempts, setAttempts] = useState<LoginAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [cleanupDate, setCleanupDate] = useState('');
  const [cleaning, setCleaning] = useState(false);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    try {
      const functions = getFunctions();
      const getLog = httpsCallable<Record<string, never>, { attempts: LoginAttempt[] }>(functions, 'getLoginAuditLog');
      const result = await getLog({});
      setAttempts(result.data.attempts);
    } catch (error) {
      console.error('Failed to load audit log:', error);
      toastRef.current.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  const handleCleanup = useCallback(() => {
    if (!cleanupDate) {
      toastRef.current.error('Select a date first');
      return;
    }

    confirmWarning({
      title: 'Clean Up Audit Logs',
      message: `This will permanently delete all login audit logs OLDER than ${new Date(cleanupDate).toLocaleDateString()}. This cannot be undone.`,
      onConfirm: async () => {
        setCleaning(true);
        try {
          const functions = getFunctions();
          const cleanup = httpsCallable<{ beforeDate: string }, { deleted: number }>(functions, 'cleanupLoginAuditLog');
          // beforeDate = start of the selected date (logs strictly before this are deleted)
          const result = await cleanup({ beforeDate: new Date(cleanupDate).toISOString() });
          toastRef.current.success(`Deleted ${result.data.deleted} log entries`);
          setCleanupDate('');
          // Refresh both summary and detail
          await fetchAuditLog();
        } catch (error) {
          console.error('Cleanup failed:', error);
          toastRef.current.error('Failed to clean up logs');
        } finally {
          setCleaning(false);
        }
      },
    });
  }, [cleanupDate, confirmWarning, fetchAuditLog]);

  // Compute IP summary
  const ipSummaries = useMemo(() => {
    const now = Date.now();
    const oneHourAgo = now - 60 * 60 * 1000;
    const sixHoursAgo = now - 6 * 60 * 60 * 1000;
    const oneDayAgo = now - 24 * 60 * 60 * 1000;

    const ipMap = new Map<string, { last24h: number; last6h: number; last1h: number }>();

    for (const a of attempts) {
      if (!a.timestamp || a.ipAddress === 'unknown') continue;
      const ts = new Date(a.timestamp).getTime();
      if (ts < oneDayAgo) continue;

      const entry = ipMap.get(a.ipAddress) || { last24h: 0, last6h: 0, last1h: 0 };
      entry.last24h++;
      if (ts >= sixHoursAgo) entry.last6h++;
      if (ts >= oneHourAgo) entry.last1h++;
      ipMap.set(a.ipAddress, entry);
    }

    const summaries: IpSummary[] = [];
    for (const [ip, counts] of ipMap) {
      summaries.push({
        ip,
        ...counts,
        suspicious: counts.last24h > 10 || counts.last1h > 5,
      });
    }

    // Sort: suspicious first, then by 24h count descending
    summaries.sort((a, b) => {
      if (a.suspicious !== b.suspicious) return a.suspicious ? -1 : 1;
      return b.last24h - a.last24h;
    });

    return summaries;
  }, [attempts]);

  const filteredAttempts = filterType === 'all'
    ? attempts
    : attempts.filter(a => a.eventType === filterType);

  const failedCount = attempts.filter(a => !a.success).length;
  const rateLimitedCount = attempts.filter(a => a.eventType === 'rate_limited').length;
  const suspiciousCount = ipSummaries.filter(s => s.suspicious).length;

  return (
    <div className="space-y-6">
      {/* Header card with refresh */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-gray-400" />
              <CardTitle>Login Audit Log</CardTitle>
              <span className="text-sm text-gray-500">(Last 7 days)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={fetchAuditLog}
                loading={loading}
                leftIcon={<RefreshCw className="h-4 w-4" />}
              >
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Summary badges */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {attempts.length} total events
          </span>
          {failedCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
              {failedCount} failed
            </span>
          )}
          {rateLimitedCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
              {rateLimitedCount} rate limited
            </span>
          )}
          {suspiciousCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <AlertTriangle className="h-3 w-3" />
              {suspiciousCount} suspicious IP{suspiciousCount > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Cleanup controls */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm text-gray-600">Delete logs older than:</span>
          <input
            type="date"
            value={cleanupDate}
            onChange={(e) => setCleanupDate(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCleanup}
            loading={cleaning}
            disabled={!cleanupDate}
            leftIcon={<Trash2 className="h-4 w-4" />}
          >
            Clean Up
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {/* IP Summary Table */}
            {ipSummaries.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">IP Activity Summary</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-medium text-gray-500">IP Address</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500">Last 24 hrs</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500">Last 6 hrs</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-500">Last 1 hr</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ipSummaries.map((summary) => (
                        <tr
                          key={summary.ip}
                          className={`border-b border-gray-100 ${summary.suspicious ? 'bg-red-50' : 'hover:bg-gray-50'}`}
                        >
                          <td className={`py-2 px-3 font-mono text-xs ${summary.suspicious ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
                            {summary.ip}
                          </td>
                          <td className={`py-2 px-3 text-right ${summary.last24h > 10 ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                            {summary.last24h}
                          </td>
                          <td className={`py-2 px-3 text-right ${summary.last6h > 8 ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                            {summary.last6h}
                          </td>
                          <td className={`py-2 px-3 text-right ${summary.last1h > 5 ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                            {summary.last1h}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {summary.suspicious ? (
                              <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Suspicious
                              </span>
                            ) : (
                              <span className="text-green-600 text-xs font-medium">Normal</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Filter */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {['all', 'password_verified', 'password_failed', 'rate_limited', 'mfa_success', 'mfa_failed', 'mfa_skipped'].map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    filterType === type
                      ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-300'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {type === 'all' ? 'All' : EVENT_CONFIG[type]?.label || type}
                </button>
              ))}
            </div>

            {/* Detail Table */}
            {filteredAttempts.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm">
                No login attempts found{filterType !== 'all' ? ' for this filter' : ''}.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Event Details</h3>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Time</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Email</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Event</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">IP Address</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAttempts.map((attempt) => {
                      const config = EVENT_CONFIG[attempt.eventType] || { label: attempt.eventType, icon: Shield, color: 'text-gray-500' };
                      const Icon = config.icon;
                      const isSuspiciousIp = ipSummaries.find(s => s.ip === attempt.ipAddress)?.suspicious;
                      return (
                        <tr key={attempt.id} className={`border-b border-gray-100 ${isSuspiciousIp ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                          <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{formatTimestamp(attempt.timestamp)}</td>
                          <td className="py-2 px-3 text-gray-900 font-medium">{attempt.email}</td>
                          <td className="py-2 px-3">
                            <span className={`inline-flex items-center gap-1.5 ${config.color}`}>
                              <Icon className="h-3.5 w-3.5" />
                              {config.label}
                            </span>
                          </td>
                          <td className={`py-2 px-3 font-mono text-xs ${isSuspiciousIp ? 'text-red-700 font-semibold' : 'text-gray-500'}`}>
                            {attempt.ipAddress}
                          </td>
                          <td className="py-2 px-3 text-gray-500 text-xs">{attempt.details || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
